const STORAGE_PREFIX = 'chatter-audio-toast:';
const ENTRY_TTL_MS = 1000 * 60 * 30; // 30 minutes
const CLIENT_ID = `${Date.now().toString(36)}-${Math.random()
  .toString(36)
  .slice(2)}`;

type AudioClaimRecord = {
  expiresAt: number;
  clientId: string;
};

const parseRecord = (value: string | null): AudioClaimRecord | null => {
  if (!value) return null;

  try {
    const parsed = JSON.parse(value) as AudioClaimRecord;
    if (
      typeof parsed?.expiresAt === 'number' &&
      typeof parsed?.clientId === 'string'
    ) {
      return parsed;
    }
  } catch (error) {
    console.warn('Failed to parse chatter audio claim record', error);
  }

  return null;
};

/**
 * Coordinates notification audio playback across browser tabs so that we only
 * play the sound once per message.
 *
 * Returns `true` when the current tab should play audio for the provided
 * `messageId`, otherwise `false`.
 */
export const shouldPlayNotificationAudio = (messageId?: string | null) => {
  if (typeof window === 'undefined' || !messageId) return true;

  try {
    const key = `${STORAGE_PREFIX}${messageId}`;
    const storage = window.localStorage;
    const existingRecord = parseRecord(storage.getItem(key));
    const now = Date.now();

    if (existingRecord) {
      if (existingRecord.expiresAt > now) {
        return false;
      }

      // Expired entry; clean it up before attempting to claim
      storage.removeItem(key);
    }

    const newRecord: AudioClaimRecord = {
      clientId: CLIENT_ID,
      expiresAt: now + ENTRY_TTL_MS
    };

    const serialised = JSON.stringify(newRecord);
    storage.setItem(key, serialised);

    // Ensure we won the race by reading the stored value back.
    const stored = storage.getItem(key);
    if (stored === serialised) {
      return true;
    }

    return false;
  } catch (error) {
    console.warn('Failed to coordinate chatter notification audio', error);
    // Fall back to playing the audio to avoid missing the notification entirely.
    return true;
  }
};

